import type { AdminQueueCountsDto } from '@carcheck/shared';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hook for the admin queue-counts rollup
 * (`GET /admin/queue-counts`, AUT-103 / ADMIN-7), targeting the AUT-103
 * backend. Reads the 15-min Redis rollup — a NORMAL GET, never
 * `?force=true` (the force path is throttled per-IP and 429s).
 *
 * Query-key convention (ADMIN-10 guardrail / queryClient.ts):
 *   `['admin', 'queue-counts']` — a single-object read, no list/detail split.
 *
 * SINGLE FETCH, TWO CONSUMERS: both the sidebar badges
 * (`QueueCountsProvider` → `useQueueCounts`) AND the dashboard count tiles
 * (`DashboardPage`) call this hook. TanStack dedupes by key, so the two
 * consumers share ONE network request + one cache entry — no double-fetch.
 *
 * `refetchInterval` gives the dashboard's "LIVE" pill real cadence; it
 * layers on top of the global `staleTime` / `refetchOnWindowFocus`
 * (queryClient.ts). The controller is ungated by permission (visible to
 * ALL admins) so no permission guard here.
 *
 * The DTO is centralised in `@carcheck/shared` — api + admin import the
 * same source of truth, no mirror drift.
 */

const QUEUE_COUNTS_BASE = '/admin/queue-counts';

/** Stable query-key for the queue-counts rollup read. */
export const ADMIN_QUEUE_COUNTS_KEY = ['admin', 'queue-counts'] as const;

/** Poll cadence for the dashboard "LIVE" feel (ms). Matches the 30s global staleTime. */
export const QUEUE_COUNTS_REFETCH_MS = 30_000;

export const useAdminQueueCounts = (): UseQueryResult<AdminQueueCountsDto, Error> =>
  useQuery({
    queryKey: ADMIN_QUEUE_COUNTS_KEY,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminQueueCountsDto>(QUEUE_COUNTS_BASE, { signal });
      return data;
    },
    refetchInterval: QUEUE_COUNTS_REFETCH_MS,
  });
